
import React, { useMemo, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePieceStore } from '@/stores/usePieceStore';
import { useCuttingStore } from '@/stores/useCuttingStore';
import { useAuthStore } from '@/stores/useAuthStore';
import { useOfflineQueue } from '@/stores/useOfflineQueue';
import { StatusBadge } from '../components/UI/StatusBadge';
import { ChevronLeft, Calendar, Shirt, Tag, Clock, Play, CheckCircle, WifiOff, ScanLine, User, Scissors } from 'lucide-react';
import { clsx } from 'clsx';
import { BottomDock } from '../components/Layout/BottomDock';

const useOnlineStatus = () => {
   const [online, setOnline] = React.useState(navigator.onLine);
   React.useEffect(() => {
      const h = () => setOnline(navigator.onLine);
      window.addEventListener('online', h);
      window.addEventListener('offline', h);
      return () => { window.removeEventListener('online', h); window.removeEventListener('offline', h); };
   }, []);
   return online;
};

export const PieceList: React.FC = () => {
   const { sampleId } = useParams();
   const navigate = useNavigate();
   const { samples, fetchAssignedSamples, loading, tailors, fetchTailors } = usePieceStore();
   const { startCutting, endCutting } = useCuttingStore();
   const { userProfile } = useAuthStore();
   const isOnline = useOnlineStatus();
   console.log('[PieceList] Render - isOnline:', isOnline, 'userRole:', userProfile?.role);

   const [isStartModalOpen, setStartModalOpen] = useState(false);
   const [isEndModalOpen, setEndModalOpen] = useState(false);
   const [dateInput, setDateInput] = useState<string>('');
   const [remarksInput, setRemarksInput] = useState<string>('');
   const [leftoverFabric, setLeftoverFabric] = useState<any[]>([]);
   const [actionLoading, setActionLoading] = useState(false);


   useEffect(() => {
      if (samples.length === 0) fetchAssignedSamples();
      fetchTailors();
   }, [samples.length]);

   const sample = useMemo(() => samples.find(s => s.id === sampleId), [samples, sampleId]);

   const allPieces = useMemo(() => sample?.sample_pieces?.sort((a, b) => a.piece_number - b.piece_number) || [], [sample]);

   // Show all pieces - no filtering
   const pieces = allPieces;

   // SKELETON LOADING — must be AFTER all hooks
   if (loading && !sample) return (
      <div className="min-h-screen bg-stone-50 flex flex-col">
         <div className="h-16 bg-white border-b border-slate-100 animate-pulse" />
         <PieceListSkeleton />
      </div>
   );

   if (!sample) return <div className="min-h-screen flex items-center justify-center text-slate-400 font-bold">Job Not Found</div>;
   const prod = sample.sample_production;
   const plan = sample.sample_planning; // Active planning row (has cutting dates)
   const style = sample.styles;
   const isCuttingIncharge = userProfile?.role === 'cutting_incharge' || userProfile?.role === 'admin';

   // Dates
   const formatDateForInput = (date?: string) => {
      const d = date ? new Date(date) : new Date();
      d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
      return d.toISOString().slice(0, 16);
   };

   const handleStartClick = () => {
      setDateInput(formatDateForInput());
      setStartModalOpen(true);
   };

   const handleEndClick = () => {
      setDateInput(formatDateForInput());
      setRemarksInput('');

      // Initialize leftover inputs from issued fabric
      if (prod?.fabric_issued && Array.isArray(prod.fabric_issued)) {
         setLeftoverFabric(prod.fabric_issued.map((f: any) => ({ ...f, leftover: '' })));
      } else {
         setLeftoverFabric([]);
      }

      setEndModalOpen(true);
   };

   const confirmStart = async () => {
      if (actionLoading) return;

      if (!isOnline) {
         await useOfflineQueue.getState().addToQueue('startCutting', {
            sampleId: sample.id || '',
            startDate: new Date(dateInput).toISOString()
         });
         setStartModalOpen(false);
         return;
      }

      if (plan?.cutting_start_at) return; // Already started

      setActionLoading(true);
      try {
         await startCutting(sample.id || '', new Date(dateInput).toISOString());
         setStartModalOpen(false);
      } catch (e: any) {
         alert(`Error: ${e.message}`);
      } finally {
         setActionLoading(false);
      }
   };

   const confirmEnd = async () => {
      if (actionLoading || !isOnline) return;
      setActionLoading(true);
      try {
         await endCutting(sample.id || '', new Date(dateInput).toISOString(), remarksInput, leftoverFabric);
         setEndModalOpen(false);
      } catch (e: any) {
         alert(`Error: ${e.message}`);
      } finally {
         setActionLoading(false);
      }
   };

   const tinyDate = (d?: string | null) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—';
   const fullTime = (d?: string | null) => d ? new Date(d).toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' }) : '—';

   return (
      <div className="min-h-screen bg-stone-50 flex flex-col font-sans text-slate-900 pb-safe">
         {/* HEADER - No Bell */}
         <div className="bg-white/80 backdrop-blur-2xl pt-safe px-5 pb-3 border-b border-slate-100/80 sticky top-0 z-40">
            <div className="flex items-center gap-3 py-3 relative">
               <button onClick={() => navigate('/')} className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 active:bg-slate-200 transition-colors">
                  <ChevronLeft size={18} />
               </button>
               <div className="flex-1 min-w-0">
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">{style?.buyer_name || 'Buyer'}</p>
                  <h1 className="text-lg font-bold text-slate-800 leading-none truncate">{style?.style_code}</h1>
               </div>
            </div>
         </div>

         {/* OFFLINE BANNER */}
         {!isOnline && (
            <div className="bg-slate-800 text-white text-[10px] font-bold py-1.5 text-center flex items-center justify-center gap-2 animate-slide-down">
               <WifiOff size={12} />
               OFFLINE MODE - Changes will sync when online
            </div>
         )}

         <div className="flex-1 px-5 pb-32 animate-page space-y-4 pt-4">

            {/* Style Detailed Info Card */}
            <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
               <div className="flex gap-3 mb-3">
                  <div className="w-14 h-18 rounded-xl bg-slate-50 border border-slate-100 overflow-hidden flex-shrink-0 flex items-center justify-center">
                     {style?.image_url ? <img src={style.image_url} alt="Style" className="w-full h-full object-cover" /> : <Shirt size={18} className="text-slate-300" />}
                  </div>
                  <div className="flex-1 space-y-1">
                     <p className="text-xs font-semibold text-slate-700 line-clamp-1">{style?.style_name}</p>
                     <div className="flex flex-wrap gap-2">
                        <span className="px-2 py-0.5 rounded-md bg-blue-50 text-blue-600 text-[9px] font-bold uppercase">{sample.sample_type}</span>
                        {sample.version_number && <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-500 text-[9px] font-bold uppercase">v{sample.version_number}</span>}
                        {plan && plan.plan_type && (
                           <span className={`px-2 py-0.5 rounded-md text-[9px] font-bold uppercase ${
                              plan.plan_type === 'fresh' ? 'bg-emerald-50 text-emerald-600' :
                              plan.plan_type === 'buyer_rework' ? 'bg-rose-50 text-rose-600' :
                              'bg-slate-50 text-slate-600'
                           }`}>
                              {plan.plan_type === 'fresh' ? 'Fresh' : 
                               plan.plan_type === 'buyer_rework' ? 'Buyer Rework' : 
                               plan.plan_type}{plan.attempt_number ? ` • Attempt ${plan.attempt_number}` : ''}
                           </span>
                        )}
                     </div>
                  </div>
               </div>
               <div className="grid grid-cols-3 gap-2 border-t border-slate-50 pt-2.5">
                  <div className="text-[10px] font-mono text-slate-600 flex flex-col gap-0.5">
                     <span className="text-[8px] text-slate-400 uppercase font-bold">Deadline</span>
                     <span className="flex items-center gap-1"><Clock size={10} className="text-slate-300" /> {sample?.planned_submission ? tinyDate(sample.planned_submission) : 'None'}</span>
                  </div>
                  <div className="text-[10px] font-mono text-slate-600 flex flex-col gap-0.5">
                     <span className="text-[8px] text-slate-400 uppercase font-bold">Qty</span>
                     <span className="flex items-center gap-1"><Tag size={10} className="text-slate-300" /> {sample.requested_qty} Pcs</span>
                  </div>
                  <div className="text-[10px] font-mono text-slate-600 flex flex-col gap-0.5">
                     <span className="text-[8px] text-slate-400 uppercase font-bold">Handover</span>
                     <span className="flex items-center gap-1"><Clock size={10} className="text-slate-300" /> {sample.file_handover_at ? tinyDate(sample.file_handover_at) : 'Pending'}</span>
                  </div>
               </div>
            </div>

            {/* CUTTING ACTIONS (Only for Cutting Incharge) */}
            {isCuttingIncharge && (
               <div className="bg-white rounded-2xl p-5 border border-blue-100 shadow-sm relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-3 opacity-10"><Calendar size={80} className="text-blue-600" /></div>
                  <h3 className="text-sm font-bold text-slate-800 mb-1">Cutting Department</h3>
                  <p className="text-xs text-slate-500 mb-4">Manage start and end times for this job.</p>

                  <div className="grid grid-cols-2 gap-3 relative z-10">
                     <button
                        onClick={handleStartClick}
                        disabled={!!plan?.cutting_start_at || !isOnline}
                        className={clsx(
                           "flex flex-col items-center justify-center gap-2 p-4 rounded-xl border border-dashed transition-all",
                           plan?.cutting_start_at
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border-blue-300 bg-blue-50 text-blue-600 active:scale-95 disabled:opacity-50 disabled:scale-100"
                        )}
                     >
                        {plan?.cutting_start_at ? <CheckCircle size={20} /> : <Play size={20} />}
                        <span className="text-xs font-bold">{plan?.cutting_start_at ? 'Started' : 'Start Job'}</span>
                     </button>

                     <button
                        onClick={handleEndClick}
                        disabled={!plan?.cutting_start_at || !!plan?.cutting_end_at || !isOnline}
                        className={clsx(
                           "flex flex-col items-center justify-center gap-2 p-4 rounded-xl border border-dashed transition-all disabled:opacity-50 disabled:scale-100",
                           plan?.cutting_end_at
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                              : !plan?.cutting_start_at
                                 ? "border-slate-200 bg-slate-50 text-slate-300 cursor-not-allowed"
                                 : "border-purple-300 bg-purple-50 text-purple-600 active:scale-95"
                        )}
                     >
                        <CheckCircle size={20} />
                        <span className="text-xs font-bold">{plan?.cutting_end_at ? 'Completed' : 'End Job'}</span>
                     </button>
                  </div>

                  {/* Explicit Times Display */}
                  <div className="mt-4 pt-4 border-t border-slate-50 grid grid-cols-2 gap-4">
                     <div>
                        <span className="text-[9px] text-slate-400 uppercase font-bold block mb-0.5">Start Time</span>
                        <p className="text-xs font-mono font-bold text-slate-700">{fullTime(plan?.cutting_start_at)}</p>
                     </div>
                     <div>
                        <span className="text-[9px] text-slate-400 uppercase font-bold block mb-0.5">End Time</span>
                        <p className="text-xs font-mono font-bold text-slate-700">{fullTime(plan?.cutting_end_at)}</p>
                     </div>
                  </div>
               </div>
            )}

            {/* Pieces List - Detailed Cards */}
            <div className="space-y-3">
               <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-1 flex items-center justify-between">
                  <span>Sample Pieces</span>
                  <span className="bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full text-[10px]">{pieces.length} Pcs</span>
               </h3>

               {pieces.map((p) => {
                  const sewCycles = Array.isArray(p.sewing_cycles) ? p.sewing_cycles : [];
                  const sewing_in = sewCycles.length > 0 ? sewCycles[0]?.started_at : null;
                  const sewing_out = sewCycles.length > 0 ? sewCycles[sewCycles.length - 1]?.ended_at : null;
                  const tailorName = p.current_tailor_name || 'Unassigned';
                  // Cutting dates from planning
                  const cuttingStart = plan?.cutting_start_at;
                  const cuttingEnd = plan?.cutting_end_at;

                  return (
                     <div key={p.id} onClick={() => navigate(`/piece/${p.id}`)} className="bg-blue-50/50 p-4 rounded-xl border border-blue-100 active:scale-[0.98] transition-all shadow-sm shadow-blue-100/20">
                        {/* Top: Status & Number */}
                        <div className="flex items-center justify-between mb-3 border-b border-blue-100/50 pb-3">
                           <div className="flex items-center gap-3">
                              <div className={clsx("w-8 h-8 rounded-lg flex items-center justify-center font-mono font-bold text-xs shadow-sm", p.status.startsWith('Completed') ? "bg-emerald-100 text-emerald-700" : "bg-white text-blue-600")}>
                                 {p.piece_number}
                              </div>
                              <div>
                                 <p className="text-xs font-bold text-slate-700">{p.size || 'Regular'}</p>
                                 <p className="text-[10px] text-blue-400 font-mono">{p.qr_code}</p>
                              </div>
                           </div>
                           <StatusBadge status={p.status} className="scale-90 origin-right" />
                        </div>

                        {/* Bottom: Details (Tailor / Cutting / Sewing Dates) */}
                        <div className="grid grid-cols-3 gap-2 text-[10px] text-slate-600">
                           <div className="flex flex-col gap-0.5">
                              <span className="text-[8px] text-slate-400 uppercase font-bold">Assigned To</span>
                              <div className="flex items-center gap-1 font-bold text-slate-700 truncate">
                                 <User size={10} className="text-blue-500 flex-shrink-0" />
                                 <span className="truncate">{tailorName}</span>
                              </div>
                           </div>

                           <div className="flex flex-col gap-0.5">
                              <span className="text-[8px] text-slate-400 uppercase font-bold">Cutting</span>
                              <div className="flex flex-col gap-0.5 font-mono">
                                 {cuttingStart ? (
                                    <span className="flex items-center gap-1 text-blue-600" title={`Start: ${new Date(cuttingStart).toLocaleString()}`}>
                                       <Play size={8} className="flex-shrink-0" /> {tinyDate(cuttingStart)}
                                    </span>
                                 ) : <span className="text-slate-300">Not started</span>}
                                 {cuttingEnd && (
                                    <span className="flex items-center gap-1 text-emerald-600" title={`End: ${new Date(cuttingEnd).toLocaleString()}`}>
                                       <CheckCircle size={8} className="flex-shrink-0" /> {tinyDate(cuttingEnd)}
                                    </span>
                                 )}
                              </div>
                           </div>

                           <div className="flex flex-col gap-0.5">
                              <span className="text-[8px] text-slate-400 uppercase font-bold">Sewing</span>
                              <div className="flex flex-col gap-0.5 font-mono">
                                 {sewing_in ? (
                                    <span className="flex items-center gap-1 text-purple-600" title={`Start: ${new Date(sewing_in).toLocaleString()}`}>
                                       <Play size={8} className="flex-shrink-0" /> {tinyDate(sewing_in)}
                                    </span>
                                 ) : <span className="text-slate-300">Not started</span>}
                                 {sewing_out && (
                                    <span className="flex items-center gap-1 text-emerald-600" title={`End: ${new Date(sewing_out).toLocaleString()}`}>
                                       <CheckCircle size={8} className="flex-shrink-0" /> {tinyDate(sewing_out)}
                                    </span>
                                 )}
                              </div>
                           </div>
                        </div>
                     </div>
                  );
               })}
            </div>

            <BottomDock sampleId={sampleId} />

            {/* Modals ... (Start/End) ... */}
            {isStartModalOpen && (
               <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4 backdrop-blur-sm animate-overlay">
                  <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl animate-modal">
                     <h3 className="text-lg font-bold text-slate-800 mb-1">Start Cutting Job</h3>
                     <div className="space-y-4 mt-4">
                        <input type="datetime-local" value={dateInput} onChange={e => setDateInput(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm font-mono" />
                        <button onClick={confirmStart} disabled={actionLoading} className="w-full py-3 bg-blue-600 text-white font-bold rounded-xl">{actionLoading ? 'Loading...' : 'Confirm Start'}</button>
                        <button onClick={() => setStartModalOpen(false)} className="w-full py-3 text-slate-400 font-bold text-xs">Cancel</button>
                     </div>
                  </div>
               </div>
            )}

            {isEndModalOpen && (
            <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4 backdrop-blur-sm animate-overlay">
               <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl animate-modal">
                  <h3 className="text-lg font-bold text-slate-800 mb-1">End Cutting Job</h3>
                  <div className="space-y-4 mt-4">
                     <input type="datetime-local" value={dateInput} onChange={e => setDateInput(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm font-mono" />

                     {Array.isArray(leftoverFabric) && leftoverFabric.length > 0 ? (
                        <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                           <label className="text-[10px] font-bold text-slate-400 uppercase">Leftover Fabric</label>
                           {leftoverFabric.map((f, idx) => (
                              <div key={idx} className="bg-slate-50 border border-slate-200 rounded-xl p-2.5">
                                 <div className="flex justify-between items-center text-xs font-bold text-slate-700 mb-1.5">
                                    <span>{f.name}</span>
                                    <span className="text-[10px] text-slate-400 bg-white px-1.5 py-0.5 rounded border border-slate-100">Issued: {f.issued}</span>
                                 </div>
                                 <input
                                    type="number"
                                    step="0.01"
                                    placeholder="Leftover Qty"
                                    value={f.leftover || ''}
                                    onChange={e => {
                                       const newArr = [...leftoverFabric];
                                       newArr[idx] = { ...newArr[idx], leftover: parseFloat(e.target.value) || 0 };
                                       setLeftoverFabric(newArr);
                                    }}
                                    className="w-full bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-xs font-mono"
                                 />
                              </div>
                           ))}
                        </div>
                     ) : (
                        <input type="text" value={(leftoverFabric as unknown) as string || ''} onChange={e => setLeftoverFabric(e.target.value as any)} placeholder="Leftover Fabric (e.g. 1.2m)" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm" />
                     )}
                     <textarea rows={3} value={remarksInput} onChange={e => setRemarksInput(e.target.value)} placeholder="Remarks..." className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm" />
                     <button onClick={confirmEnd} disabled={actionLoading} className="w-full py-3 bg-emerald-600 text-white font-bold rounded-xl">{actionLoading ? 'Loading...' : 'Complete Job'}</button>
                     <button onClick={() => setEndModalOpen(false)} className="w-full py-3 text-slate-400 font-bold text-xs">Cancel</button>
                  </div>
               </div>
            </div>
            )}
         </div>
      </div>
   );
};

// ... Skeleton & Hook ...
const PieceListSkeleton = () => (
   <div className="space-y-4 animate-pulse px-5 pt-4">
      <div className="h-32 bg-slate-200 rounded-2xl w-full" />
      <div className="space-y-3">
         {[1, 2, 3].map(i => <div key={i} className="h-24 bg-slate-100 rounded-xl w-full" />)}
      </div>
   </div>
);
